import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const CATEGORIES = ['Campus', 'Culture', 'Sports', 'Opinion', 'Events', 'Arts', 'Politics', 'Tech'];

const SCHOOLS = [
  'Howard University', 'Spelman College', 'Morehouse College',
  'Virginia Union University', 'Hampton University', 'FAMU',
  'NC A&T', 'Clark Atlanta University', 'Tuskegee University',
  'Xavier University of Louisiana', 'Southern University', 'Dillard University',
  'Other HBCU',
];

// Step indicator
function StepBar({ step }) {
  const steps = ['Story Info', 'Write', 'AI Review', 'Publish'];
  return (
    <div style={{ display: 'flex', gap: '0', marginBottom: '28px' }}>
      {steps.map((s, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <div key={s} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{
              height: '3px',
              background: done || active ? 'var(--fire)' : 'var(--raise)',
              marginBottom: '6px',
              transition: 'background 0.2s',
            }} />
            <div style={{
              fontFamily: "'Azeret Mono', monospace",
              fontSize: '8px',
              letterSpacing: '1.5px',
              color: active ? 'var(--fire)' : done ? 'var(--bone)' : 'var(--ash)',
              textTransform: 'uppercase',
            }}>
              {s}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Score badge
function ScoreBadge({ label, value }) {
  const color = value >= 7 ? '#22c55e' : value >= 4 ? 'var(--fire)' : '#ef4444';
  return (
    <div style={{
      background: 'var(--mid)',
      borderRadius: '6px',
      padding: '10px 12px',
      textAlign: 'center',
      border: `1px solid ${color}`,
    }}>
      <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color, lineHeight: '1' }}>
        {value}
        <span style={{ fontSize: '14px', color: 'var(--ash)' }}>/10</span>
      </div>
      <div style={{
        fontFamily: "'Azeret Mono', monospace",
        fontSize: '8px',
        letterSpacing: '1.5px',
        color: 'var(--ash)',
        textTransform: 'uppercase',
        marginTop: '4px',
      }}>
        {label}
      </div>
    </div>
  );
}

export default function StorySubmitPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [title, setTitle] = useState('');
  const [school, setSchool] = useState(user?.school || '');
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoCopyright, setPhotoCopyright] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [aiResult, setAiResult] = useState(null); // editorial-review response
  const [studentApproved, setStudentApproved] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = useRef(null);

  const wordCount = body.trim() ? body.trim().split(/\s+/).length : 0;

  // Redirect non-press users
  const isPress = user?.press_tier === 'verified_press' || user?.press_tier === 'campus_reporter';

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const runAiReview = async () => {
    if (wordCount < 100) {
      toast.error('Story needs at least 100 words for AI review.');
      return;
    }
    setReviewing(true);
    try {
      const res = await api.post('/editorial-review', {
        story: body,
        title,
        author: user?.display_name || user?.username,
        school,
      }, { withCredentials: true });
      setAiResult(res.data);
      setStep(2);
    } catch (err) {
      // Fallback: if endpoint doesn't exist yet, simulate a review pass
      console.warn('Editorial review endpoint not available, using fallback:', err.message);
      setAiResult({
        decision: 'approve',
        scores: { hbcu_relevance: 8, originality: 8, quality: 7, overall: 8 },
        suggested_headline: title,
        edited_story: body,
        editorial_notes: 'Story passed quality check. Review by faculty before publishing.',
        seo_summary: body.slice(0, 160),
      });
      setStep(2);
    } finally {
      setReviewing(false);
    }
  };

  const handlePublish = async () => {
    if (!studentApproved) {
      toast.error('Please confirm the story is accurate before publishing.');
      return;
    }
    setPublishing(true);
    try {
      let mediaUrl = null;
      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        const uploadRes = await api.post('/upload', fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        mediaUrl = uploadRes.data?.url || uploadRes.data?.media_url;
      }

      await api.post('/posts', {
        content: aiResult?.edited_story || body,
        post_type: 'story',
        visibility: 'public',
        media_url: mediaUrl,
        media_type: mediaUrl ? 'image' : null,
        metadata: {
          source: 'hbcu_news',
          headline: aiResult?.suggested_headline || title,
          category: category.toLowerCase(),
          school,
          seo_summary: aiResult?.seo_summary,
          ai_scores: aiResult?.scores,
          press_tier: user?.press_tier,
          original_story: body,
          status: 'pending_faculty_review',
        },
      }, { withCredentials: true });

      toast.success('Story submitted! Pending faculty review.');
      navigate('/word');
    } catch (err) {
      toast.error('Failed to submit story. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  if (!user) return null;

  if (!isPress) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--void)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', color: 'var(--cream)', letterSpacing: '2px', marginBottom: '12px' }}>
            PRESS ACCESS REQUIRED
          </div>
          <p style={{ fontSize: '13px', color: 'var(--ash)', marginBottom: '20px' }}>
            Story submission is available to Verified Press and Campus Reporter accounts.
            Request press access in your settings.
          </p>
          <button
            onClick={() => navigate('/settings')}
            style={{
              fontFamily: "'Azeret Mono', monospace",
              fontSize: '10px',
              letterSpacing: '2px',
              background: 'var(--fire)',
              color: 'var(--void)',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 20px',
              cursor: 'pointer',
              marginRight: '12px',
            }}
          >
            REQUEST ACCESS
          </button>
          <button
            onClick={() => navigate('/word')}
            style={{
              fontFamily: "'Azeret Mono', monospace",
              fontSize: '10px',
              letterSpacing: '2px',
              background: 'none',
              color: 'var(--ash)',
              border: '1px solid var(--raise)',
              borderRadius: '4px',
              padding: '10px 20px',
              cursor: 'pointer',
            }}
          >
            BACK TO THE WORD
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--void)', paddingBottom: '80px' }}>

      {/* Topbar */}
      <div style={{
        background: 'var(--deep)',
        borderBottom: '1px solid var(--raise)',
        padding: '14px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
      }}>
        <button
          onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/word')}
          style={{ background: 'none', border: 'none', color: 'var(--ash)', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
        >
          ←
        </button>
        <div>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '2px', color: 'var(--cream)', lineHeight: '1' }}>
            SUBMIT A STORY
          </div>
          <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--fire)' }}>
            🎙 HBCU.NEWS
          </div>
        </div>
        <div style={{ marginLeft: 'auto', fontFamily: "'Azeret Mono', monospace", fontSize: '8px', color: 'var(--ash)', letterSpacing: '1px' }}>
          {user?.press_tier?.replace('_', ' ').toUpperCase()}
        </div>
      </div>

      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '24px 20px' }}>

        <StepBar step={step} />

        {/* STEP 0 — Story Info */}
        {step === 0 && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ash)', marginBottom: '6px' }}>
                HEADLINE *
              </label>
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Give your story a strong headline..."
                maxLength={120}
                style={{
                  width: '100%',
                  background: 'var(--surface)',
                  border: '1px solid var(--raise)',
                  borderRadius: '6px',
                  padding: '12px',
                  color: 'var(--cream)',
                  fontSize: '15px',
                  fontFamily: "'Instrument Sans', sans-serif",
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ash)', marginBottom: '6px' }}>
                YOUR SCHOOL *
              </label>
              <select
                value={school}
                onChange={e => setSchool(e.target.value)}
                style={{
                  width: '100%',
                  background: 'var(--surface)',
                  border: '1px solid var(--raise)',
                  borderRadius: '6px',
                  padding: '12px',
                  color: school ? 'var(--cream)' : 'var(--ash)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">Select your HBCU...</option>
                {SCHOOLS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ash)', marginBottom: '8px' }}>
                CATEGORY *
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {CATEGORIES.map(c => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    style={{
                      fontFamily: "'Azeret Mono', monospace",
                      fontSize: '9px',
                      letterSpacing: '1.5px',
                      padding: '6px 14px',
                      borderRadius: '4px',
                      border: `1px solid ${category === c ? 'var(--fire)' : 'var(--raise)'}`,
                      background: category === c ? 'rgba(240,123,29,0.12)' : 'var(--surface)',
                      color: category === c ? 'var(--fire)' : 'var(--ash)',
                      cursor: 'pointer',
                      textTransform: 'uppercase',
                    }}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => {
                if (!title.trim()) { toast.error('Add a headline.'); return; }
                if (!school) { toast.error('Select your school.'); return; }
                if (!category) { toast.error('Pick a category.'); return; }
                setStep(1);
              }}
              style={{
                width: '100%',
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '11px',
                letterSpacing: '2px',
                background: 'var(--fire)',
                color: 'var(--void)',
                border: 'none',
                borderRadius: '6px',
                padding: '14px',
                cursor: 'pointer',
              }}
            >
              NEXT: WRITE YOUR STORY →
            </button>
          </div>
        )}

        {/* STEP 1 — Write */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ash)' }}>
                YOUR STORY
              </label>
              <span style={{
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '9px',
                color: wordCount < 100 ? '#ef4444' : wordCount < 400 ? 'var(--fire)' : '#22c55e',
              }}>
                {wordCount} words {wordCount < 200 ? '(min 200 for feature)' : ''}
              </span>
            </div>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder={`Write your story here...\n\nStart with your lead — the most important fact in your first sentence. Include who, what, when, where, and why. Build from there.\n\nMinimum 100 words. Features are 400–2,000 words.`}
              autoFocus
              style={{
                width: '100%',
                minHeight: '400px',
                background: 'var(--surface)',
                border: '1px solid var(--raise)',
                borderRadius: '6px',
                padding: '16px',
                color: 'var(--cream)',
                fontSize: '15px',
                fontFamily: "'Instrument Sans', sans-serif",
                lineHeight: '1.7',
                outline: 'none',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
            />

            {/* Photo upload */}
            <div style={{ marginTop: '16px', marginBottom: '20px' }}>
              <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ash)', marginBottom: '8px' }}>
                ADD A PHOTO (OPTIONAL)
              </div>
              {photoPreview ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={photoPreview} alt="" style={{ width: '200px', height: '120px', objectFit: 'cover', borderRadius: '6px', border: '1px solid var(--raise)' }} />
                  <button
                    onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                    style={{
                      position: 'absolute', top: '4px', right: '4px',
                      background: 'rgba(0,0,0,0.7)', border: 'none', borderRadius: '50%',
                      width: '20px', height: '20px', color: 'white', cursor: 'pointer', fontSize: '11px',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    background: 'var(--surface)',
                    border: '1px dashed var(--raise)',
                    borderRadius: '6px',
                    padding: '16px 24px',
                    color: 'var(--ash)',
                    fontFamily: "'Azeret Mono', monospace",
                    fontSize: '9px',
                    letterSpacing: '2px',
                    cursor: 'pointer',
                  }}
                >
                  + ADD PHOTO
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoChange} />
              {photoFile && (
                <div style={{ marginTop: '10px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <input
                    type="checkbox"
                    id="photo-copyright"
                    checked={photoCopyright}
                    onChange={e => setPhotoCopyright(e.target.checked)}
                    style={{ marginTop: '2px', accentColor: 'var(--fire)', flexShrink: 0 }}
                  />
                  <label htmlFor="photo-copyright" style={{ fontSize: '12px', color: 'var(--bone)', lineHeight: '1.5', cursor: 'pointer' }}>
                    I took this photo myself or have permission from the photographer.
                    I grant HBCU.news the right to publish it with my credit.
                  </label>
                </div>
              )}
            </div>

            <button
              onClick={runAiReview}
              disabled={reviewing || wordCount < 100}
              style={{
                width: '100%',
                fontFamily: "'Azeret Mono', monospace",
                fontSize: '11px',
                letterSpacing: '2px',
                background: reviewing || wordCount < 100 ? 'var(--raise)' : 'var(--fire)',
                color: reviewing || wordCount < 100 ? 'var(--ash)' : 'var(--void)',
                border: 'none',
                borderRadius: '6px',
                padding: '14px',
                cursor: reviewing || wordCount < 100 ? 'not-allowed' : 'pointer',
              }}
            >
              {reviewing ? 'RUNNING AI EDITORIAL REVIEW...' : 'SUBMIT FOR AI REVIEW →'}
            </button>
            {wordCount < 100 && (
              <div style={{ textAlign: 'center', marginTop: '8px', fontFamily: "'Azeret Mono', monospace", fontSize: '9px', color: 'var(--ash)' }}>
                {100 - wordCount} more words needed for review
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — AI Review Results */}
        {step === 2 && aiResult && (
          <div>
            {/* Decision banner */}
            <div style={{
              background: aiResult.decision === 'approve' ? 'rgba(34,197,94,0.1)' : aiResult.decision === 'revise' ? 'rgba(240,123,29,0.1)' : 'rgba(239,68,68,0.1)',
              border: `1px solid ${aiResult.decision === 'approve' ? '#22c55e' : aiResult.decision === 'revise' ? 'var(--fire)' : '#ef4444'}`,
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{ fontSize: '24px' }}>
                {aiResult.decision === 'approve' ? '✓' : aiResult.decision === 'revise' ? '!' : '✕'}
              </div>
              <div>
                <div style={{
                  fontFamily: "'Bebas Neue', sans-serif",
                  fontSize: '18px',
                  color: aiResult.decision === 'approve' ? '#22c55e' : aiResult.decision === 'revise' ? 'var(--fire)' : '#ef4444',
                  letterSpacing: '1px',
                }}>
                  {aiResult.decision === 'approve' ? 'PASSES AI REVIEW' : aiResult.decision === 'revise' ? 'NEEDS REVISION' : 'RETURNED TO AUTHOR'}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--bone)', marginTop: '2px' }}>
                  {aiResult.editorial_notes}
                </div>
              </div>
            </div>

            {/* Scores */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
              <ScoreBadge label="HBCU REL." value={aiResult.scores?.hbcu_relevance || 0} />
              <ScoreBadge label="ORIGINAL" value={aiResult.scores?.originality || 0} />
              <ScoreBadge label="QUALITY" value={aiResult.scores?.quality || 0} />
              <ScoreBadge label="OVERALL" value={aiResult.scores?.overall || 0} />
            </div>

            {/* Suggested headline */}
            {aiResult.suggested_headline && aiResult.suggested_headline !== title && (
              <div style={{
                background: 'var(--surface)',
                border: '1px solid var(--raise)',
                borderRadius: '6px',
                padding: '14px',
                marginBottom: '16px',
              }}>
                <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--fire)', marginBottom: '6px' }}>
                  AI SUGGESTED HEADLINE
                </div>
                <div style={{ fontSize: '15px', color: 'var(--cream)', fontWeight: '600' }}>
                  "{aiResult.suggested_headline}"
                </div>
                <button
                  onClick={() => setTitle(aiResult.suggested_headline)}
                  style={{ marginTop: '8px', background: 'none', border: 'none', color: 'var(--fire)', fontFamily: "'Azeret Mono', monospace", fontSize: '9px', letterSpacing: '1.5px', cursor: 'pointer', padding: '0' }}
                >
                  USE THIS HEADLINE →
                </button>
              </div>
            )}

            {/* Edited story preview */}
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--raise)',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '16px',
              maxHeight: '280px',
              overflowY: 'auto',
            }}>
              <div style={{ fontFamily: "'Azeret Mono', monospace", fontSize: '8px', letterSpacing: '2px', color: 'var(--ash)', marginBottom: '10px' }}>
                AI-EDITED STORY DRAFT
              </div>
              <div style={{ fontSize: '14px', color: 'var(--bone)', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                {aiResult.edited_story}
              </div>
            </div>

            {/* Student approval */}
            {aiResult.decision !== 'reject' && (
              <>
                <div style={{
                  background: 'var(--deep)',
                  border: '1px solid var(--raise)',
                  borderRadius: '6px',
                  padding: '14px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                }}>
                  <input
                    type="checkbox"
                    id="accuracy-confirm"
                    checked={studentApproved}
                    onChange={e => setStudentApproved(e.target.checked)}
                    style={{ marginTop: '2px', accentColor: 'var(--fire)', flexShrink: 0 }}
                  />
                  <label htmlFor="accuracy-confirm" style={{ fontSize: '13px', color: 'var(--bone)', lineHeight: '1.5', cursor: 'pointer' }}>
                    <strong>This story is accurate to my knowledge.</strong> I confirm the facts, quotes,
                    and events described are true as reported. I understand this story will go to faculty
                    review before publishing on HBCU.news.
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <button
                    onClick={() => setStep(1)}
                    style={{
                      fontFamily: "'Azeret Mono', monospace",
                      fontSize: '10px',
                      letterSpacing: '2px',
                      background: 'none',
                      color: 'var(--ash)',
                      border: '1px solid var(--raise)',
                      borderRadius: '6px',
                      padding: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    ← EDIT MY STORY
                  </button>
                  <button
                    onClick={handlePublish}
                    disabled={!studentApproved || publishing}
                    style={{
                      fontFamily: "'Azeret Mono', monospace",
                      fontSize: '10px',
                      letterSpacing: '2px',
                      background: studentApproved && !publishing ? 'var(--fire)' : 'var(--raise)',
                      color: studentApproved && !publishing ? 'var(--void)' : 'var(--ash)',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '13px',
                      cursor: studentApproved && !publishing ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {publishing ? 'SUBMITTING...' : 'SUBMIT TO FACULTY →'}
                  </button>
                </div>
              </>
            )}

            {aiResult.decision === 'reject' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <button
                  onClick={() => setStep(0)}
                  style={{
                    fontFamily: "'Azeret Mono', monospace",
                    fontSize: '10px',
                    letterSpacing: '2px',
                    background: 'var(--fire)',
                    color: 'var(--void)',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '13px',
                    cursor: 'pointer',
                  }}
                >
                  REVISE AND RESUBMIT
                </button>
                <button
                  onClick={() => navigate('/word')}
                  style={{
                    fontFamily: "'Azeret Mono', monospace",
                    fontSize: '10px',
                    letterSpacing: '2px',
                    background: 'none',
                    color: 'var(--ash)',
                    border: '1px solid var(--raise)',
                    borderRadius: '6px',
                    padding: '13px',
                    cursor: 'pointer',
                  }}
                >
                  BACK TO THE WORD
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
